import {
  COMPLAINTS_CONFIG,
  COMPLAINTS_PROVIDER,
  type ReclamationFormValues,
} from '../data/reclamaciones';

export type FormErrors = Partial<Record<keyof ReclamationFormValues, string>>;

export type ReclamationPayload = ReturnType<typeof buildReclamationPayload>;

export type SubmissionResult = {
  codigo: string;
  fechaRegistro: string;
  correoNotificacionEnviado: boolean;
  correoConstanciaEnviado: boolean;
};

type ApiResponse = {
  ok: boolean;
  message?: string;
  data?: SubmissionResult;
};

function validateDocument(type: string, value: string): string | null {
  const normalized = value.trim();
  if (type === 'DNI' && !/^\d{8}$/.test(normalized)) return 'El DNI debe contener exactamente 8 dígitos.';
  if (type === 'RUC' && !/^\d{11}$/.test(normalized)) return 'El RUC debe contener exactamente 11 dígitos.';
  if ((type === 'Carné de extranjería' || type === 'Pasaporte') && !/^[A-Za-z0-9-]{4,20}$/.test(normalized)) {
    return 'Ingresa entre 4 y 20 letras, números o guiones.';
  }
  if (normalized.length < 4 || normalized.length > 20) return 'Ingresa un número de documento válido.';
  return null;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9+()\-\s]{7,20}$/;

export function getPeruDateInputValue(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export function validateReclamation(values: ReclamationFormValues): FormErrors {
  const errors: FormErrors = {};
  const required = <K extends keyof ReclamationFormValues>(key: K, message: string, min = 1) => {
    const value = values[key];
    if (typeof value === 'string' && value.trim().length < min) errors[key] = message;
  };

  required('nombres', 'Ingresa tus nombres.', 2);
  required('apellidos', 'Ingresa tus apellidos.', 2);
  required('numeroDocumento', 'Ingresa tu número de documento.', 4);
  const consumerDocumentError = validateDocument(values.tipoDocumento, values.numeroDocumento);
  if (consumerDocumentError) errors.numeroDocumento = consumerDocumentError;

  if (!phonePattern.test(values.telefono.trim())) errors.telefono = 'Ingresa un teléfono válido.';
  if (!emailPattern.test(values.correo.trim())) errors.correo = 'Ingresa un correo electrónico válido.';
  required('direccion', 'Ingresa tu domicilio.', 5);
  required('departamentoId', 'Selecciona un departamento.');
  required('provinciaId', 'Selecciona una provincia.');
  required('distritoId', 'Selecciona un distrito.');
  if (!/^\d{6}$/.test(values.ubigeo)) errors.distritoId = 'Selecciona un distrito válido.';

  if (values.esMenorEdad) {
    required('nombresRepresentante', 'Ingresa los nombres y apellidos del representante.', 3);
    required('numeroDocumentoRepresentante', 'Ingresa el documento del representante.', 4);
    const representativeDocumentError = validateDocument(
      values.tipoDocumentoRepresentante,
      values.numeroDocumentoRepresentante,
    );
    if (representativeDocumentError) errors.numeroDocumentoRepresentante = representativeDocumentError;
    if (!phonePattern.test(values.telefonoRepresentante.trim())) errors.telefonoRepresentante = 'Ingresa un teléfono válido.';
    if (!emailPattern.test(values.correoRepresentante.trim())) errors.correoRepresentante = 'Ingresa un correo válido.';
    required('direccionRepresentante', 'Ingresa el domicilio del representante.', 5);
  }

  required('descripcionBienServicio', 'Describe el producto o servicio.', 5);
  if (values.fechaAdquisicion) {
    if (!isValidIsoDate(values.fechaAdquisicion)) {
      errors.fechaAdquisicion = 'Ingresa una fecha válida.';
    } else if (values.fechaAdquisicion > getPeruDateInputValue()) {
      errors.fechaAdquisicion = 'La fecha no puede ser futura.';
    }
  }
  if (values.montoReclamado && !/^\d+(?:[.,]\d{1,2})?$/.test(values.montoReclamado.trim())) {
    errors.montoReclamado = 'Ingresa un monto válido, por ejemplo: 120.50.';
  }
  if (values.tipoComprobante !== 'Sin comprobante' && values.numeroComprobante.trim().length < 2) {
    errors.numeroComprobante = 'Ingresa el número o referencia del comprobante.';
  }
  required('detalle', 'Describe lo ocurrido con al menos 20 caracteres.', 20);
  required('pedido', 'Indica claramente qué solución solicitas.', 10);
  if (!values.declaracionVeracidad) errors.declaracionVeracidad = 'Debes confirmar que la información es verdadera.';
  if (!values.aceptaPrivacidad) errors.aceptaPrivacidad = 'Debes confirmar que leíste el aviso de privacidad.';

  return errors;
}

export function buildReclamationPayload(values: ReclamationFormValues, files: File[]) {
  const amount = values.montoReclamado.trim()
    ? Number(values.montoReclamado.replace(',', '.'))
    : null;

  return {
    schemaVersion: '1.0',
    fechaRegistroCliente: new Date().toISOString(),
    proveedor: COMPLAINTS_PROVIDER,
    consumidor: {
      nombres: values.nombres.trim(),
      apellidos: values.apellidos.trim(),
      tipoDocumento: values.tipoDocumento,
      numeroDocumento: values.numeroDocumento.trim(),
      telefono: values.telefono.trim(),
      correo: values.correo.trim(),
      domicilio: {
        direccion: values.direccion.trim(),
        departamento: values.departamento,
        provincia: values.provincia,
        distrito: values.distrito,
        ubigeo: values.ubigeo,
      },
      esMenorEdad: values.esMenorEdad,
      representante: values.esMenorEdad
        ? {
            nombres: values.nombresRepresentante.trim(),
            tipoDocumento: values.tipoDocumentoRepresentante,
            numeroDocumento: values.numeroDocumentoRepresentante.trim(),
            telefono: values.telefonoRepresentante.trim(),
            correo: values.correoRepresentante.trim(),
            direccion: values.direccionRepresentante.trim(),
          }
        : null,
    },
    relacionConsumo: {
      tipoBien: values.tipoBien,
      descripcion: values.descripcionBienServicio.trim(),
      fechaAdquisicion: values.fechaAdquisicion || null,
      montoReclamado: amount,
      moneda: values.moneda,
      tipoComprobante: values.tipoComprobante,
      numeroComprobante: values.tipoComprobante === 'Sin comprobante'
        ? null
        : values.numeroComprobante.trim(),
    },
    registro: {
      tipo: values.tipoRegistro,
      detalle: values.detalle.trim(),
      pedido: values.pedido.trim(),
      medioRespuesta: values.medioRespuesta,
    },
    declaraciones: {
      informacionVerdadera: values.declaracionVeracidad,
      avisoPrivacidadLeido: values.aceptaPrivacidad,
    },
    adjuntos: files.map((file) => ({
      nombre: file.name,
      tipo: file.type,
      tamanioBytes: file.size,
      ultimaModificacion: file.lastModified,
    })),
  };
}

export async function submitReclamation(
  payload: ReclamationPayload,
  files: File[],
  requestId: string,
  honeypot: string,
): Promise<SubmissionResult> {
  const body = new FormData();
  body.append('datos', JSON.stringify(payload));
  body.append('request_id', requestId);
  body.append('website', honeypot);
  files.forEach((file) => body.append('archivos[]', file, file.name));

  let response: Response;
  try {
    response = await fetch(COMPLAINTS_CONFIG.endpoint, { method: 'POST', body });
  } catch {
    throw new Error('No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.');
  }

  let result: ApiResponse;
  try {
    result = (await response.json()) as ApiResponse;
  } catch {
    throw new Error('El servidor devolvió una respuesta que no pudo interpretarse.');
  }

  if (!response.ok || !result.ok || !result.data) {
    throw new Error(result.message || 'No fue posible registrar el reclamo o queja.');
  }

  return result.data;
}
